import React from 'react';
import { Box, CircularProgress, IconButton, InputBase, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Tag } from '@/components/ui/tag';

type SearchableTagSelectorProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchEnter?: () => void;
  searchInputRef?: React.Ref<HTMLInputElement>;
  autoFocus?: boolean;
  searchPlaceholder: string;
  isSearching?: boolean;
  isAdding?: boolean;
  addingOption?: string | null;
  addSectionTitle: string;
  currentSectionTitle: string;
  filteredOptions: string[];
  canAddCustom: boolean;
  customOptionLabel?: string;
  onAddOption: (value: string) => void;
  selectedValues: string[];
  onRemoveSelected: (value: string) => void;
  showRemoveSelected?: boolean;
  resolveLabel?: (value: string) => string;
  renderOptionMeta?: (value: string) => React.ReactNode;
  renderSelectedValue?: (
    value: string,
    helpers: {
      displayLabel: string;
      onRemove: () => void;
      removeAriaLabel: string;
    }
  ) => React.ReactNode;
  emptyOptionsText: string;
  emptyOptionsExtra?: React.ReactNode;
  emptySelectedText: string;
  addAriaLabel?: (displayLabel: string) => string;
  removeAriaLabel?: (displayLabel: string) => string;
  listMaxHeight?: number;
  appearance?: 'default' | 'ingredient-modal';
};

export const SearchableTagSelector: React.FC<SearchableTagSelectorProps> = ({
  searchValue,
  onSearchChange,
  onSearchEnter,
  searchInputRef,
  autoFocus = false,
  searchPlaceholder,
  isSearching = false,
  isAdding = false,
  addingOption = null,
  addSectionTitle,
  currentSectionTitle,
  filteredOptions,
  canAddCustom,
  customOptionLabel,
  onAddOption,
  selectedValues,
  onRemoveSelected,
  showRemoveSelected = true,
  resolveLabel,
  renderOptionMeta,
  renderSelectedValue,
  emptyOptionsText,
  emptyOptionsExtra,
  emptySelectedText,
  addAriaLabel,
  removeAriaLabel,
  listMaxHeight = 210,
  appearance = 'default',
}) => {
  const toDisplayLabel = React.useCallback(
    (value: string) => (resolveLabel ? resolveLabel(value) : value),
    [resolveLabel],
  );

  const handleSearchKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key !== 'Enter' || !onSearchEnter) return;
      event.preventDefault();
      onSearchEnter();
    },
    [onSearchEnter],
  );

  const normalizedCustomLabel = (customOptionLabel ?? searchValue).trim();
  const normalizedAddingOption = String(addingOption ?? '').trim().toLowerCase();
  const isIngredientModal = appearance === 'ingredient-modal';

  return (
    <>
      <Box
        sx={{
          px: isIngredientModal ? 1.3 : 1.1,
          py: isIngredientModal ? 0.72 : 0.85,
          borderRadius: '12px',
          border: '1px solid #D0D5DD',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: '#F9FAFB',
          minHeight: isIngredientModal ? 38 : undefined,
        }}
      >
        {isSearching ? (
          <CircularProgress size={16} sx={{ color: '#20AEDC', flexShrink: 0 }} />
        ) : isAdding ? (
          <CircularProgress size={16} sx={{ color: '#20AEDC', flexShrink: 0 }} />
        ) : (
          <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A2B3', flexShrink: 0 }} />
        )}
        <InputBase
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          inputRef={searchInputRef}
          autoFocus={autoFocus}
          disabled={isAdding}
          placeholder={searchPlaceholder}
          sx={{
            flex: 1,
            fontFamily: 'Inter, sans-serif',
            fontSize: isIngredientModal ? '0.88rem' : '0.94rem',
            color: '#1F2937',
            '& input::placeholder': { color: '#98A2B3', opacity: 1 },
          }}
        />
      </Box>

      <Typography
        sx={{
          mt: isIngredientModal ? 1.15 : 1.35,
          fontFamily: 'Inter, sans-serif',
          fontSize: isIngredientModal ? '1rem' : '0.75rem',
          fontWeight: 700,
          color: isIngredientModal ? '#1E2939' : '#1F2937',
        }}
      >
        {addSectionTitle}
      </Typography>

      <Box
        sx={{
          mt: isIngredientModal ? 0.55 : 0.7,
          borderRadius: '12px',
          border: isIngredientModal ? '1px solid #F3F4F6' : '1px solid #E4E7EC',
          maxHeight: listMaxHeight,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#20AEDC', borderRadius: 999 },
        }}
      >
        {canAddCustom && normalizedCustomLabel ? (
          <Box
            sx={{
              px: 1.4,
              py: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: filteredOptions.length ? '1px solid #EAECF0' : 'none',
            }}
          >
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#344054', lineHeight: 1.3 }}>
              {normalizedCustomLabel}
            </Typography>
            <IconButton
              aria-label={addAriaLabel ? addAriaLabel(normalizedCustomLabel) : `Add ${normalizedCustomLabel}`}
              disabled={isAdding}
              onClick={() => onAddOption(normalizedCustomLabel)}
              sx={{
                width: 26,
                height: 26,
                bgcolor: '#20AEDC',
                color: '#fff',
                '&:hover': { bgcolor: '#1297c4' },
                '&.Mui-disabled': { bgcolor: '#B8E8F5', color: '#fff' },
              }}
            >
              {isAdding && normalizedAddingOption === normalizedCustomLabel.toLowerCase() ? (
                <CircularProgress size={14} sx={{ color: '#fff' }} />
              ) : (
                <AddRoundedIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Box>
        ) : null}

        {isSearching && filteredOptions.length === 0 && !canAddCustom ? (
          <Box
            sx={{
              px: 2,
              py: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={22} sx={{ color: '#20AEDC' }} />
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#98A2B3' }}>
              Searching…
            </Typography>
          </Box>
        ) : null}

        {filteredOptions.map((option, index) => {
          const displayLabel = toDisplayLabel(option);
          const normalizedOption = String(option ?? '').trim().toLowerCase();
          const normalizedDisplayLabel = displayLabel.trim().toLowerCase();
          const isOptionAdding = isAdding
            && (normalizedAddingOption === normalizedOption || normalizedAddingOption === normalizedDisplayLabel);
          const isOptionDisabled = isAdding && !isOptionAdding;
          return (
            <Box
              key={option}
              sx={{
                px: 1.5,
                py: isIngredientModal ? 1.0 : 0.8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                opacity: isOptionDisabled ? 0.55 : 1,
                borderBottom:
                  index === filteredOptions.length - 1 && !canAddCustom
                    ? 'none'
                    : '1px solid #F3F4F6',
              }}
            >
              <Box sx={{ minWidth: 0, pr: 1, flex: '1 1 auto' }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', color: '#364153', lineHeight: 1.3 }}>
                  {displayLabel}
                </Typography>
                {renderOptionMeta ? renderOptionMeta(option) : null}
              </Box>
              <IconButton
                aria-label={addAriaLabel ? addAriaLabel(displayLabel) : `Add ${displayLabel}`}
                disabled={isOptionDisabled}
                onClick={() => onAddOption(option)}
                sx={{
                  width: 26,
                  height: 26,
                  flexShrink: 0,
                  bgcolor: '#20AEDC',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1297c4' },
                  '&.Mui-disabled': { bgcolor: '#B8E8F5', color: '#fff' },
                }}
              >
                {isOptionAdding ? (
                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                ) : (
                  <AddRoundedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>
          );
        })}

        {!canAddCustom && filteredOptions.length === 0 && !isSearching ? (
          <Box
            sx={{
              px: 2,
              py: 2.2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: emptyOptionsExtra ? 1.25 : 0,
            }}
          >
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#98A2B3', textAlign: 'center' }}>
              {emptyOptionsText}
            </Typography>
            {emptyOptionsExtra}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ mt: isIngredientModal ? 2.0 : 1.35, borderTop: '1px solid #EAECF0' }} />

      <Typography
        sx={{
          mt: isIngredientModal ? 1.6 : 1.25,
          fontFamily: 'Inter, sans-serif',
          fontSize: isIngredientModal ? '1rem' : '0.75rem',
          fontWeight: 700,
          color: isIngredientModal ? '#1E2939' : '#1F2937',
        }}
      >
        {currentSectionTitle}
      </Typography>

      <Box
        sx={{
          mt: 0.9,
          display: 'grid',
          gridTemplateColumns: renderSelectedValue
            ? {
                xs: 'repeat(1, minmax(0, 1fr))',
                sm: 'repeat(2, minmax(0, 1fr))',
              }
            : {
                xs: 'repeat(1, minmax(0, 1fr))',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
              },
          gap: renderSelectedValue ? 0.65 : 0.8,
        }}
      >
        {selectedValues.length ? (
          selectedValues.map((value) => {
            const displayLabel = toDisplayLabel(value);
            const removeLabel = removeAriaLabel ? removeAriaLabel(displayLabel) : `Remove ${displayLabel}`;
            if (renderSelectedValue) {
              return (
                <Box key={value} sx={{ minWidth: 0 }}>
                  {renderSelectedValue(value, {
                    displayLabel,
                    onRemove: () => onRemoveSelected(value),
                    removeAriaLabel: removeLabel,
                  })}
                </Box>
              );
            }

            return (
              <Box
                key={value}
                sx={{
                  minWidth: 0,
                }}
              >
                <Tag
                  variant="outlined"
                  size={isIngredientModal ? 'md' : 'sm'}
                  maxWidth="100%"
                  className="w-full justify-between rounded-lg border-[#D0D5DD] bg-white font-[Inter,sans-serif] font-medium text-[#344054]"
                  onRemove={showRemoveSelected ? () => onRemoveSelected(value) : undefined}
                  removeAriaLabel={removeLabel}
                >
                  {displayLabel}
                </Tag>
              </Box>
            );
          })
        ) : (
          <Typography
            sx={{
              gridColumn: '1 / -1',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.78rem',
              color: '#98A2B3',
              textAlign: 'center',
              py: 0.6,
            }}
          >
            {emptySelectedText}
          </Typography>
        )}
      </Box>
    </>
  );
};
